import { createSignal } from '@react-rxjs/utils'
import { catchError, filter, from, map, mergeMap, Observable, of, share, take, tap } from 'rxjs'

import {
  FavoriteProductsDocument,
  FavoriteProductSkusDocument,
  SetFavoriteProductDocument,
  SetFavoriteProductMutation,
  SetFavoriteProductMutationVariables,
} from '../../../graphql/search'
import { logAndCaptureException } from '../../../utils/errorTools'
import { isRight, left, Result, right } from '../../../utils/result'
import { latestAuthStateWithAccessToken$ } from '../../auth/state'
import { latestCommaClient$ } from '../../graphql/apollo'
import { openFavoritesPopover } from './popover'

export interface PendingFavorite {
  parentProductSku: string
  productName?: string | null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/**
 * Decode the pending favorite out of an Auth0 `appState`, whose index signature types every field
 * as `any`.
 */
export const isPendingFavorite = (value: unknown): value is PendingFavorite =>
  isRecord(value) &&
  typeof value.parentProductSku === 'string' &&
  value.parentProductSku.length > 0 &&
  (value.productName == null || typeof value.productName === 'string')

const [pendingFavoriteSignal$, setPendingFavorite] = createSignal<PendingFavorite>()

export { setPendingFavorite }

/**
 * Left carries the favorite that failed to save, so the consumer can name the product in its error
 * toast.
 */
export type PendingFavoriteResult = Result<PendingFavorite, PendingFavorite>

/**
 * Replays the favorite stashed before login. Emits once per replay, as a Left if the save failed,
 * and not at all if the login did not take. The popover opens here to mirror an in-page add,
 * while subscribers own the error toast.
 */
export const pendingFavorite$: Observable<PendingFavoriteResult> = pendingFavoriteSignal$.pipe(
  mergeMap((pending) =>
    latestAuthStateWithAccessToken$.pipe(
      // `latestAuthStateWithAccessToken$` replays its latest value, and the signal fires from
      // `onRedirectCallback` as soon as `isLoading` clears — before the access-token leg has
      // emitted for the new session. Taking the replayed value would read the stale pre-login
      // state, so filter first and wait for an authenticated one, which arrives when the token
      // lands and re-emits the combined state.
      //
      // A login that did not take never emits an authenticated state, so this waits forever. That
      // is deliberate: re-login leaves the site for Auth0 and returns through a fresh page load,
      // discarding this subscription, and the user has already been told the login failed.
      filter((authState) => authState.isAuthenticated),
      take(1),
      mergeMap(() => latestCommaClient$.pipe(take(1))),
      mergeMap((commaClient) =>
        from(
          commaClient.mutate<SetFavoriteProductMutation, SetFavoriteProductMutationVariables>({
            mutation: SetFavoriteProductDocument,
            variables: { parentProductSku: pending.parentProductSku, isFavorite: true },
            context: { useLatestTokenIfLoggedIn: true },
            refetchQueries: [
              {
                query: FavoriteProductSkusDocument,
                context: { useLatestTokenIfLoggedIn: true },
              },
              { query: FavoriteProductsDocument, context: { useLatestTokenIfLoggedIn: true } },
            ],
            awaitRefetchQueries: true,
          }),
        ),
      ),
      map(({ errors }) => (errors?.length ? left(pending) : right(pending))),
      tap((result) => {
        if (isRight(result)) {
          openFavoritesPopover()
        }
      }),
      catchError((err) => {
        logAndCaptureException(err)
        return of(left(pending))
      }),
    ),
  ),
  // the mutation is a side effect, so a second subscriber must not replay it
  share(),
)
